import { Directive } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { BraintreeApplePayComponentDef } from '@df/module-braintree/apple-pay/braintree-apple-pay.component.def';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { IAddressData } from '@df/session/api/address-book/address';
import each from 'lodash-es/each';

const SHIPPING_METHOD_SEPARATOR = 'shippingseparator';

@Directive()
export abstract class BraintreeApplePayExpressComponentDef extends BraintreeApplePayComponentDef {
    protected lastShippingMethods?: ApplePay.IShippingMethod[];

    /**
     * Returns if click is allowed
     * @returns {boolean}
     */
    protected override allowClick(): boolean {
        return true;
    }

    /**
     * Returns available shipping methods for apple pay
     * @returns {ApplePay.IShippingMethod[]}
     */
    protected getShippingMethods(): ApplePay.IShippingMethod[] {
        const methods = this.checkoutFormComponent['nonCollectionShippingMethods'] || [];
        return methods.map(shippingMethod => {
            return {
                label: shippingMethod.title,
                amount: shippingMethod.price.toString(),
                identifier: this.getShippingMethodIdentifier(shippingMethod.code),
                detail: shippingMethod.description || ''
            };
        });
    }

    /**
     * Returns shipping type options for apple pay
     * @returns {ApplePay.ShippingType[]}
     */
    protected getShippingType(): ApplePay.ShippingType[] {
        return [ApplePay.ShippingType.shipping];
    }

    /**
     * Required fields for shipping address
     * @returns {ApplePay.ContactField[]}
     */
    protected getRequiredShippingAddressFields(): ApplePay.ContactField[] {
        const fields = [ApplePay.ContactField.name, ApplePay.ContactField.phone, ApplePay.ContactField.email];
        if (!this.isVirtual()) {
            fields.push(ApplePay.ContactField.postalAddress);
        }
        return fields;
    }

    /**
     * Required fields for billing address
     * @returns {ApplePay.ContactField[]}
     */
    protected getRequiredBillingAddressFields(): ApplePay.ContactField[] {
        return [ApplePay.ContactField.postalAddress];
    }

    /**
     * Shipping address data to use for preselection
     * @returns {ApplePay.IContact}
     */
    protected getShippingContact(): ApplePay.IContact {
        return this.getContactFromAddress(this.checkoutFormComponent.shippingAddress);
    }

    /**
     * Billing address data to use for preselection
     * @returns {ApplePay.IContact}
     */
    protected getBillingContact(): ApplePay.IContact {
        return this.getContactFromAddress(this.checkoutFormComponent.billingAddress);
    }

    /**
     * Converts address to apple contact
     * @param {IAddressData} address
     * @returns {ApplePay.IContact}
     */
    protected getContactFromAddress(address?: IAddressData): ApplePay.IContact {
        return address ? this.applePayService.getContactFromAddress(address) : {};
    }

    /**
     * Converts apple contact to address
     * @param {ApplePay.IContact} contact
     * @returns {IAddressData}
     */
    protected getAddressFromContact(contact?: ApplePay.IContact): IAddressData | undefined {
        return contact ? this.applePayService.getAddressFromContact(contact) : undefined;
    }

    /**
     * Returns checkout submission data
     * @param {ApplePay.IPaymentEvent} paymentAuthorizedEvent
     * @returns {Checkout.ISubmissionData}
     */
    protected override getSubmissionData(paymentAuthorizedEvent: ApplePay.IPaymentEvent): Checkout.ISubmissionData {
        const { phoneNumber, emailAddress } = paymentAuthorizedEvent.payment.shippingContact as ApplePay.IContact;
        const billingAddress = this.getAddressFromContact({ ...paymentAuthorizedEvent.payment.billingContact, phoneNumber });
        const shippingAddress = this.isVirtual() ? undefined : this.getAddressFromContact(paymentAuthorizedEvent.payment.shippingContact);
        return {
            quote_id: this.checkoutFormComponent['backendData'].quote.quote_id,
            billing_address: billingAddress,
            shipping_address: shippingAddress,
            email: emailAddress,
            custom: this.checkoutFormComponent['data'].custom,
            paymentMethod: {
                method: this.methodCode
            }
        };
    }

    /**
     * Adds apple pay session instance events callbacks
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected override addApplePaySessionInstanceEvents(applePaySessionInstance: ApplePay.ISessionInstance): void {
        super.addApplePaySessionInstanceEvents(applePaySessionInstance);
        applePaySessionInstance.onpaymentmethodselected = (event: ApplePay.IPaymentMethodSelectedEvent) => {
            this.onPaymentMethodSelected(event, applePaySessionInstance);
        };

        applePaySessionInstance.onshippingmethodselected = (event: ApplePay.IShippingMethodSelectedEvent) => {
            this.onShippingMethodSelected(event, applePaySessionInstance);
        };

        applePaySessionInstance.onshippingcontactselected = (event: ApplePay.IShippingContactSelectedEvent) => {
            this.onShippingContactSelected(event, applePaySessionInstance);
        };
    }

    /**
     * On apple pay payment method selected callback
     * @param {ApplePay.IPaymentMethodSelectedEvent} validateMerchanEvent
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected onPaymentMethodSelected(
        event: ApplePay.IPaymentMethodSelectedEvent,
        applePaySessionInstance: ApplePay.ISessionInstance
    ): void {
        if (this.inProgressStatus.busy) {
            applePaySessionInstance.completePaymentMethodSelection(this.getPaymentMethodUpdate());
        }
    }

    /**
     * On apple pay shipping method selected callback
     * @param {ApplePay.IShippingMethodSelectedEvent} validateMerchanEvent
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected onShippingMethodSelected(
        event: ApplePay.IShippingMethodSelectedEvent,
        applePaySessionInstance: ApplePay.ISessionInstance
    ): void {
        if (this.isVirtual()) {
            if (this.inProgressStatus.busy) {
                applePaySessionInstance.completeShippingMethodSelection(this.getShippingMethodUpdate());
            }
        } else {
            const shippingMethod = event.shippingMethod;
            const currentlyAvailable = !!this.lastShippingMethods?.find(i => i.identifier === shippingMethod.identifier);
            if (currentlyAvailable) {
                const shippingMethodCode = shippingMethod ? this.getShippingMethodCodeFromIdentifier(shippingMethod.identifier) : '';

                this.selectShippingMethod(shippingMethodCode);
                const complete = () => {
                    if (this.inProgressStatus.busy) {
                        applePaySessionInstance.completeShippingMethodSelection(this.getShippingMethodUpdate());
                    }
                };
                (<any>this.checkoutFormComponent)._saveShippingMethod(true, complete, complete);
            } else if (this.inProgressStatus.busy) {
                applePaySessionInstance.completeShippingMethodSelection(this.getShippingMethodUpdate());
            }
        }
    }

    /**
     * On apple pay shipping contact selected callback
     * @param {ApplePay.IShippingContactSelectedEvent} validateMerchanEvent
     * @param {ApplePay.ISessionInstance} applePaySessionInstance
     */
    protected onShippingContactSelected(
        event: ApplePay.IShippingContactSelectedEvent,
        applePaySessionInstance: ApplePay.ISessionInstance
    ): void {
        if (this.isVirtual()) {
            if (this.inProgressStatus.busy) {
                applePaySessionInstance.completeShippingMethodSelection(this.getShippingContactUpdate([]));
            }
        } else {
            const shippingContact = event.shippingContact;
            const shippingAddress = this.applePayService.getAddressFromContact(shippingContact);
            if (!this.checkoutFormComponent['data'].address.shipping) {
                this.checkoutFormComponent['data'].address.shipping = {};
            }
            const shippingData = this.checkoutFormComponent['data'].address.shipping;
            each(shippingData, (value, key) => {
                delete shippingData[key];
            });

            each(shippingAddress, (value, key) => {
                shippingData[key] = value;
            });
            const complete = () => {
                const methodsUpdateRequired = this.shippingMethodsUpdateRequired();
                if (!methodsUpdateRequired) {
                    if (this.inProgressStatus.busy) {
                        applePaySessionInstance.completeShippingContactSelection(this.getShippingContactUpdate());
                    }
                } else {
                    const newMethods = this.getShippingMethods();
                    this.lastShippingMethods = newMethods;
                    const completeMethod = () => {
                        if (this.inProgressStatus.busy) {
                            applePaySessionInstance.completeShippingContactSelection(this.getShippingContactUpdate(newMethods));
                        }
                    };
                    const shippingMethod = newMethods?.[0];
                    const shippingMethodCode = shippingMethod ? this.getShippingMethodCodeFromIdentifier(shippingMethod.identifier) : '';
                    this.selectShippingMethod(shippingMethodCode);
                    (<any>this.checkoutFormComponent)._saveShippingMethod(true, completeMethod, completeMethod);
                }
            };
            (<any>this.checkoutFormComponent)._saveShippingAddress(true, complete, complete);
        }
    }

    /**
     * Sets new shipping method
     * @param method
     */
    protected selectShippingMethod(method?: string): void {
        this.checkoutFormComponent['data'].shipping.method = method;
        delete this.checkoutFormComponent['data'].shipping.delivery_date;
        const additionalData = this.checkoutFormComponent['data'].shipping.additionalData;
        if (additionalData) {
            each(additionalData, (value, key) => {
                delete additionalData[key];
            });
        }
    }

    /**
     * Returns payment method update
     * @returns {ApplePay.IPaymentMethodUpdate}
     */
    protected getPaymentMethodUpdate(): ApplePay.IPaymentMethodUpdate {
        return {
            newTotal: this.getTotalLine(),
            newLineItems: this.getLineItems()
        };
    }

    /**
     * Returns shipping method update
     * @returns {ApplePay.IShippingMethodUpdate}
     */
    protected getShippingMethodUpdate(): ApplePay.IShippingMethodUpdate {
        return {
            newTotal: this.getTotalLine(),
            newLineItems: this.getLineItems()
        };
    }

    /**
     * Returns shipping contact update
     * @returns {ApplePay.IShippingContactUpdate}
     */
    protected getShippingContactUpdate(newMethods?: ApplePay.IShippingMethod[]): ApplePay.IShippingContactUpdate {
        const update: ApplePay.IShippingContactUpdate = {
            newTotal: this.getTotalLine(),
            newLineItems: this.getLineItems()
        };
        if (newMethods) {
            update.newShippingMethods = newMethods;
        }

        return update;
    }

    /**
     * Returns totals lines (except grand total, it is providede in total)
     * @returns {ApplePay.ILineItem}
     */
    protected getLineItems(): ApplePay.ILineItem[] {
        const lines = [this.getDiscountLine(), this.getShippingLine()].filter(i => !!i) as ApplePay.ILineItem[];

        if (lines.length) {
            const subtotalLine = this.getSubtotalLine();
            if (subtotalLine) {
                lines.unshift(subtotalLine);
            }
        }

        return lines;
    }

    /**
     * Returns subtotal line item
     * @returns {ApplePay.ILineItem}
     */
    protected getSubtotalLine(): ApplePay.ILineItem | undefined {
        const value = this.getTotals()?.subtotal;
        if (value) {
            return this.getLineItem('Subtotal', value);
        }
        return undefined;
    }

    /**
     * Returns shipping line item
     * @returns {ApplePay.ILineItem}
     */
    protected getShippingLine(): ApplePay.ILineItem | undefined {
        if (this.checkoutFormComponent['data']?.shipping?.method && !this.isVirtual()) {
            const value = this.getTotals()?.shipping;
            if (value || value === 0) {
                return this.getLineItem('Shipping', value);
            }
        }
        return undefined;
    }

    /**
     * Returns discount line item
     * @returns {ApplePay.ILineItem}
     */
    protected getDiscountLine(): ApplePay.ILineItem | undefined {
        const value = this.getTotals()?.discount;
        if (value) {
            return this.getLineItem('Discount', value);
        }
        return undefined;
    }

    /**
     * Returns tax line item
     * @returns {ApplePay.ILineItem}
     */
    protected getTaxLine(): ApplePay.ILineItem | undefined {
        const value = this.getTotals()?.tax;
        if (value) {
            return this.getLineItem('Tax', value);
        }
        return undefined;
    }

    protected getLineItem(label: string, amount: number, type = ApplePay.LineItemType.final): ApplePay.ILineItem {
        return {
            label,
            amount: amount.toString(),
            type
        };
    }

    /**
     * Returns apple pay request data used to create apple pay payment request with braintree
     * @returns {Braintree.IApplePayPaymentRequest}
     */
    protected override getPaymentRequestData(): Braintree.IApplePayPaymentRequest | undefined {
        const requestdata = super.getPaymentRequestData();
        if (requestdata) {
            requestdata.billingContact = this.getBillingContact();
            requestdata.requiredBillingContactFields = this.getRequiredBillingAddressFields();
            requestdata.requiredShippingContactFields = this.getRequiredShippingAddressFields();
            if (!this.isVirtual()) {
                requestdata.shippingContact = this.getShippingContact();
                requestdata.shippingType = this.getShippingType();
                const shippingMethods = this.getShippingMethods();
                requestdata.shippingMethods = shippingMethods;
                this.lastShippingMethods = shippingMethods;
            }
            requestdata.lineItems = this.getLineItems();
        }
        return requestdata;
    }

    /**
     * Returns if order is virtual (no shipping)
     * @returns {boolean}
     */
    protected isVirtual(): boolean {
        return this.checkoutFormComponent.isVirtual;
    }

    /**
     * Clear method called on apple pay close
     */
    protected override clear(): void {
        super.clear();
        this.lastShippingMethods = undefined;
    }

    /**
     * Checks if shipping methods should be updated
     * @returns {boolean}
     */
    protected shippingMethodsUpdateRequired(): boolean {
        if (this.isVirtual()) {
            return false;
        }

        if (!this.lastShippingMethods) {
            return true;
        }

        const lastShippingMethodsWithCodes = this.lastShippingMethods.map(i => {
            return { ...i, identifier: this.getShippingMethodCodeFromIdentifier(i.identifier) };
        });
        const shippingMethodsWithCodes = this.getShippingMethods()?.map(i => {
            return { ...i, identifier: this.getShippingMethodCodeFromIdentifier(i.identifier) };
        });

        return JSON.stringify(lastShippingMethodsWithCodes) !== JSON.stringify(shippingMethodsWithCodes);
    }

    /**
     * Creates apple pay shipping method identifier
     * @param {string} code
     * @returns {string}
     */
    protected getShippingMethodIdentifier(code: string): string {
        return code + SHIPPING_METHOD_SEPARATOR + Date.now().toString();
    }

    /**
     * Extract shipping method code from apple pay shipping method identifier
     * @param {string} identifier
     * @returns {string | undefined}
     */
    protected getShippingMethodCodeFromIdentifier(identifier: string): string | undefined {
        const parts = identifier.split(SHIPPING_METHOD_SEPARATOR);
        if (parts?.length) {
            return parts[0];
        }
        return undefined;
    }
}
